<template>
  <section class="tw-mb-4">
    <div class="tw-flex tw-flex-col tw-gap-2">
      <branding-company-logo-uploader-index
        v-show="!isLoading"
        ref="brandingCompanyLogoRef"
        @logo-to-submit="handleUploadedLogo"
        @logo-removed="handleRemovedLogo"
        @uploaded-logos-loaded="resetFormValidationState"
      />

      <div class="tw-flex tw-flex-row tw-gap-2 tw-justify-end">
        <authorisation-wrapper
          :resource="ADMINISTRATORS"
          :action="WRITE"
          v-slot="{ isAllowed: isWriteAllowed }"
        >
          <cl-button
            v-if="isLoading"
            variant="secondary-outline"
            disabled
            :data-test-id="'msp-branding-restore-default-button-loading'"
          >
            <cl-spinner
              :loading-text="$t('loading')"
              :size="'small'"
              class="mr-2"
            />
            <span>{{ $t("restore-default-logo-button") }}</span>
          </cl-button>
          <cl-button
            v-else
            :disabled="!isRestoreBrandingAvailable"
            variant="secondary-outline"
            @click="isRestoreModalVisible = true"
            :data-test-id="'msp-branding-restore-default-button'"
            :readonly="
              !isWriteAllowed ||
              (authUserRole === 'admin' && authUserLevel === 'msp')
            "
          >
            <span>{{ $t("restore-default-logo-button") }}</span>
          </cl-button>
          <cl-button
            v-if="isLoading"
            :variant="'secondary'"
            disabled
            :data-test-id="'msp-branding-apply-button-loading'"
          >
            <cl-spinner
              :loading-text="$t('loading')"
              :size="'small'"
              class="mr-2"
            />
            <span>{{ $t("apply-my-logo") }}</span>
          </cl-button>
          <cl-button
            v-else
            :readonly="
              !isWriteAllowed ||
              (authUserRole === 'admin' && authUserLevel === 'msp')
            "
            variant="secondary"
            :disabled="isApplyButtonDisabled"
            @click="submit"
            :data-test-id="'msp-branding-apply-my-logo-button'"
          >
            <span>{{ $t("apply-my-logo") }}</span>
          </cl-button>
        </authorisation-wrapper>
      </div>
    </div>
  </section>
  <t-delete-confirm
    :visible="isRestoreModalVisible"
    :loading="isLoading"
    @on-close="isRestoreModalVisible = false"
  >
    <template #title>
      <cl-heading :size="'h4'">{{
        $t("restore-default-logo-title")
      }}</cl-heading>
    </template>
    <template #body>
      <div class="tw-relative">
        <cl-p>
          {{ $t("restore-default-company-logo") }}
        </cl-p>
      </div>
    </template>
    <template #confirm>
      <cl-button
        variant="danger"
        data-test-id="delete-confirmation-modal-delete"
        @on-click="handleRestore"
      >
        <cl-spinner
          :loading-text="$t('loading')"
          v-if="isLoading"
          :size="'small'"
          class="tw-mr-2"
        />
        <span>{{ t("restore-default-logo-button") }}</span>
      </cl-button>
    </template>
  </t-delete-confirm>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, reactive } from "vue";
import { useI18n } from "vue-i18n";
import { useAuthenticationStore } from "@/store/authentication.store";
import BrandingCompanyLogoUploaderIndex from "@/modules/Settings/views/Msp/Branding/branding-company-logo/branding-company-logo-uploader.index.vue";
import { useVuelidate } from "@vuelidate/core";
import { useToastStore } from "@/components/TToasts/toast.store.ts";
import { useBrandingStore } from "@/store/branding.store";
import TDeleteConfirm from "@/modals/TDeleteConfirm.vue";
import { DEFAULT_BRANDING_LIGHT_LOGO_NAME } from "@/modules/Settings/constants";
import { ADMINISTRATORS, AuthorisationWrapper, WRITE } from "@/authorisation";
import { trackEvent } from "@/utils/track-event";

const { t } = useI18n();
const authStore = useAuthenticationStore();
const brandingStore = useBrandingStore();
const toastStore = useToastStore();

type ChildComponentExposed = {
  resetLogos: () => void;
  initLogos: () => Promise<void>;
  applyLogos: () => Promise<void>;
};

const brandingCompanyLogoRef = ref<ChildComponentExposed | null>(null);
const isApplyModalVisible = ref<boolean>(false);
const isLoading = ref<boolean>(true);
const isRestoreModalVisible = ref<boolean>(false);

const emit = defineEmits<{
  (e: "has-changed", value: boolean): void;
}>();

const selectedMsp = computed(() => authStore.selectedMsp);
const selectedAccount = computed(() => authStore.selectedAccount);
const isRestoreBrandingAvailable = computed(() => {
  const formIsSaved = !v$.value.logosForm.$anyDirty;

  const brandingIsNotDefault =
    savedBranding.value.light_logo !==
      `/assets/${DEFAULT_BRANDING_LIGHT_LOGO_NAME}` ||
    savedBranding.value.dark_logo !== "";
  return formIsSaved && brandingIsNotDefault;
});

const isApplyButtonDisabled = computed(() => {
  const logosForm = v$.value.logosForm;
  const formValues = form.logosForm;

  const isFormInvalid =
    logosForm.light_logo.$invalid || logosForm.dark_logo.$invalid;

  const isFormUntouched =
    !logosForm.light_logo.$dirty && !logosForm.dark_logo.$dirty;

  const logosUnchanged =
    formValues.light_logo === savedBranding.value.light_logo &&
    formValues.dark_logo === savedBranding.value.dark_logo;

  return (
    isFormInvalid ||
    isFormUntouched ||
    logosUnchanged ||
    (form.logosForm.light_logo === "" && form.logosForm.dark_logo === "")
  );
});

const authUser = computed(() => authStore.authUser);
const authUserRole = computed(() => authUser.value?.role);
const authUserLevel = computed(() => authUser.value?.level);

const getInitialFormState = () => ({
  dark_logo: "",
  light_logo: "",
});

const form = reactive({
  logosForm: getInitialFormState(),
});

const rules = {
  logosForm: {
    dark_logo: {},
    light_logo: {},
  },
};

const v$ = useVuelidate(rules, form);

const savedBranding = ref(getInitialFormState());

const handleUploadedLogo = (response: {
  logoUrl: string;
  mode: "light" | "dark";
}) => {
  if (response.mode === "light") {
    form.logosForm.light_logo = response.logoUrl;
  } else if (response.mode === "dark") {
    form.logosForm.dark_logo = response.logoUrl;
  }
  v$.value.logosForm.$touch();
  emit("has-changed", true);
  return;
};

const handleRemovedLogo = (mode: "light" | "dark") => {
  if (mode === "light") {
    form.logosForm.light_logo = "";
  } else if (mode === "dark") {
    form.logosForm.dark_logo = "";
  }
  v$.value.logosForm.$touch();
  emit("has-changed", true);
  return;
};

const handleRestore = async () => {
  try {
    isLoading.value = true;
    await brandingStore.restoreLogo({
      organisationUuid:
        selectedMsp?.value?.uuid || selectedAccount?.value?.uuid,
    });

    form.logosForm.light_logo = "";
    form.logosForm.dark_logo = "";

    v$.value.logosForm.$reset();
    savedBranding.value = {
      light_logo: `/assets/${DEFAULT_BRANDING_LIGHT_LOGO_NAME}`,
      dark_logo: "",
    };

    brandingCompanyLogoRef.value?.resetLogos();

    toastStore.displayToast({
      title: t("success"),
      message: t("changes-saved"),
      duration: 5000,
      variant: "success",
      testId: "branding-restore-toast-success",
    });
    emit("has-changed", false);
    trackEvent({
      category: "Branding",
      action: "Restore logos",
      name: "",
    });
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e);
  } finally {
    isLoading.value = false;
    isRestoreModalVisible.value = false;
  }
};

const submit = async () => {
  const isValid = await v$.value.logosForm.$validate();
  if (!isValid) return;

  try {
    isLoading.value = true;

    let formPayload: {};
    // The reason for this logic is that when one logo is uploaded, its supposed
    // to be used for both dark and light mode. To set one logo for both cases
    // the logo is supposed to be sent for both values in payload. When one logo is empty
    // its value in form is equal to ""
    formPayload = {
      light_logo:
        form.logosForm.light_logo ===
        `/assets/${DEFAULT_BRANDING_LIGHT_LOGO_NAME}`
          ? ""
          : form.logosForm.light_logo
            ? form.logosForm.light_logo
            : form.logosForm.dark_logo,
      dark_logo: form.logosForm.dark_logo
        ? form.logosForm.dark_logo
        : form.logosForm.light_logo,
    };

    await brandingStore.saveBrandingLogo({
      organisationUuid:
        selectedMsp?.value?.uuid || selectedAccount?.value?.uuid,
      form: formPayload,
    });

    const brandingResponse = await brandingStore.getBranding({
      organisationUuid:
        selectedMsp?.value?.uuid || selectedAccount?.value?.uuid,
    });

    form.logosForm.light_logo = brandingResponse.logo;
    form.logosForm.dark_logo = brandingResponse.logo_dark;

    savedBranding.value = {
      light_logo: brandingResponse.logo,
      dark_logo: brandingResponse.logo_dark,
    };
    await brandingCompanyLogoRef.value?.applyLogos();

    v$.value.logosForm.$reset();
    toastStore.displayToast({
      title: t("success"),
      message: t("company-logo-branding-applied"),
      duration: 5000,
      variant: "success",
      testId: "branding-save-toast-success",
    });
    emit("has-changed", false);
    trackEvent({
      category: "Branding",
      action: "Save logos",
      name: "",
    });
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e);
  } finally {
    isApplyModalVisible.value = false;
    isLoading.value = false;
  }
};

onMounted(async () => {
  isLoading.value = true;
  const brandingResponse = await brandingStore.getBranding({
    organisationUuid: selectedMsp?.value?.uuid || selectedAccount?.value?.uuid,
  });

  const uploadedLogos = await brandingStore.getUploadedLogos({
    organisationUuid: selectedMsp?.value?.uuid || selectedAccount?.value?.uuid,
  });

  if (brandingResponse.logo_dark) {
    form.logosForm.dark_logo = brandingResponse.logo_dark;
    savedBranding.value.dark_logo = brandingResponse.logo_dark;
  }

  if (brandingResponse.logo) {
    form.logosForm.light_logo = brandingResponse.logo;
    savedBranding.value.light_logo = brandingResponse.logo;
  }

  await brandingCompanyLogoRef.value?.initLogos();

  const brandingLightLogo = brandingResponse.logo;
  const brandingDarkLogo = brandingResponse.logo_dark;

  const uploadedLightLogo = uploadedLogos.find(
    (logo: string) =>
      logo?.startsWith("assets/branding/") && logo.includes("/light_"),
  );
  const uploadedDarkLogo = uploadedLogos.find(
    (logo: string) =>
      logo?.startsWith("assets/branding/") && logo.includes("/dark_"),
  );

  // Determine if uploaded images are the ones currently used in branding
  // or they are just uploaded but not applied images -
  // apply button is then active on mounted
  if (
    brandingLightLogo === uploadedLightLogo &&
    brandingDarkLogo === uploadedDarkLogo
  ) {
    v$.value.logosForm.$reset();
  } else if (!uploadedLightLogo && !uploadedDarkLogo) {
    v$.value.logosForm.$reset();
  } else {
    emit("has-changed", true);
    v$.value.logosForm.$touch();
  }
  isLoading.value = false;
});

// This is needed to reset form validation state after branding logo
// are fetched from backend
const resetFormValidationState = () => {
  v$.value.logosForm.$reset();
};
</script>
